import { api } from "@convex/_generated/api";
import { useQuery } from "@tanstack/react-query";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
const locationsGetAllMineRef = api.locations.getAllMine;

import { convexQuery } from "@convex-dev/react-query";
import { useSearch } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";

import { Button } from "~/components/ui/button";
import { DateRangeInput } from "~/components/ui/date-range-input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useSearchParamsDateRange } from "~/utils/search-params-date-range";

// Report types from current sales layout
const REPORT_TYPES = [
  // Core Reports (our new streamlined structure)
  { value: "menu-performance", label: "Menu Performance", category: "Core" },
  {
    value: "employee-performance",
    label: "Employee Performance",
    category: "Core",
  },
  {
    value: "employee-earnings-summary",
    label: "Employee Earnings Summary",
    category: "Core",
  },
  {
    value: "employee-tip-report",
    label: "Employee Tip Report",
    category: "Core",
  },
  {
    value: "employee-sales-summary",
    label: "Employee Sales Summary",
    category: "Core",
  },
  { value: "labor-summary", label: "Labor Summary", category: "Core" },
  {
    value: "sales-by-staff-summary",
    label: "Sales by Staff Summary",
    category: "Core",
  },
  {
    value: "transaction-analysis",
    label: "Transaction Analysis",
    category: "Core",
  },
  { value: "cash-operations", label: "Cash Operations", category: "Core" },
  {
    value: "tax-collected-report",
    label: "Tax Collected Report",
    category: "Core",
  },
  {
    value: "detailed-order-type-report",
    label: "Detailed Order Type Report",
    category: "Core",
  },

  // Legacy Reports (keep for now during transition)
];

// Group by options for specific reports
const GROUP_BY_OPTIONS: Record<string, { value: string; label: string }[]> = {
  "menu-performance": [
    { value: "item", label: "By Item" },
    { value: "category", label: "By Category" },
    { value: "menu", label: "By Menu" },
  ],
  "employee-performance": [
    { value: "daily-sales", label: "Daily Sales" },
    { value: "weekly-earnings", label: "Weekly Earnings" },
    { value: "tips", label: "Tips" },
  ],
  "transaction-analysis": [
    { value: "orders", label: "By Orders" },
    { value: "payments", label: "By Payments" },
    { value: "refunds", label: "By Refunds" },
    { value: "voids", label: "By Voids" },
    { value: "taxes", label: "By Taxes" },
    { value: "product-mix", label: "By Product Mix" },
  ],
  "cash-operations": [
    { value: "drawer-balances", label: "Drawer Balances" },
    { value: "sales-projections", label: "Sales Projections" },
    { value: "sales-summary", label: "Sales Summary" },
  ],
};

interface ReportsHeaderProps {
  selectedReportType?: string;
  selectedLocationId?: string;
  onReportTypeChange: (reportType: string) => void;
  onLocationChange: (locationId: string) => void;
  onGroupByChange?: (groupBy: string) => void;
  // Dynamic faceted filters based on selected report
  facetedFilters?: React.ReactNode[];
}

const DEFAULT_FACETED_FILTERS: React.ReactNode[] = [];

export const ReportsHeader = ({
  selectedReportType,
  selectedLocationId,
  onReportTypeChange,
  onLocationChange,
  onGroupByChange,
  facetedFilters = DEFAULT_FACETED_FILTERS,
}: ReportsHeaderProps) => {
  const [dateRange, setDateRange] = useSearchParamsDateRange();
  const searchParams = useSearch({ strict: false });

  // Get locations for the organization
  const { data: locations = [] } = useQuery(
    convexQuery(locationsGetAllMineRef, {})
  );

  const selectedLocation = locations.find((r) => r.id === selectedLocationId);

  // Get current groupBy from URL
  const currentGroupBy =
    searchParams.groupBy ??
    GROUP_BY_OPTIONS[selectedReportType ?? ""]?.[0]?.value;

  // Check if current report supports group by
  const groupByOptions = selectedReportType
    ? GROUP_BY_OPTIONS[selectedReportType]
    : undefined;

  const handleGroupByChange = (groupBy: string) => {
    onGroupByChange?.(groupBy);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Report Type Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="text-foreground aria-expanded:bg-accent gap-2 rounded-md"
            size="sm"
            type="button"
            variant="outline"
          >
            {REPORT_TYPES.find((r) => r.value === selectedReportType)?.label ??
              "Report Types"}
            <ChevronDown className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {["Core", "Legacy"].map((category) => (
            <div key={category}>
              <div className="caption-strong text-muted-foreground px-2 py-1.5 tracking-wider uppercase">
                {category}
              </div>
              {REPORT_TYPES.filter(
                (report) => report.category === category
              ).map((report) => (
                <DropdownMenuItem
                  className={
                    selectedReportType === report.value ? "bg-muted" : ""
                  }
                  key={report.value}
                  onClick={() => onReportTypeChange(report.value)}
                >
                  {report.label}
                </DropdownMenuItem>
              ))}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Location Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="text-foreground aria-expanded:bg-accent gap-2 rounded-md"
            size="sm"
            type="button"
            variant="outline"
          >
            {selectedLocation ? selectedLocation.name : "Select Location"}
            <ChevronDown className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {locations.map((location) => (
            <DropdownMenuItem
              className={selectedLocationId === location.id ? "bg-muted" : ""}
              key={location.id}
              onClick={() => onLocationChange(location.id)}
            >
              {location.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Group By Dropdown (conditional) */}
      {groupByOptions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="text-foreground aria-expanded:bg-accent gap-2 rounded-md"
              size="sm"
              type="button"
              variant="outline"
            >
              {groupByOptions.find((opt) => opt.value === currentGroupBy)
                ?.label ?? "Group By"}
              <ChevronDown className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            {groupByOptions.map((option) => (
              <DropdownMenuItem
                className={currentGroupBy === option.value ? "bg-muted" : ""}
                key={option.value}
                onClick={() => handleGroupByChange(option.value)}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Date Range Picker */}
      <DateRangeInput
        allowPastDates={true}
        onValueChange={(v) => v.from && v.to && setDateRange(v.from, v.to)}
        value={{
          from: dateRange.from,
          to: dateRange.to,
        }}
      />

      {/* Dynamic Faceted Filters */}
      {facetedFilters.map((filter, index) => (
        <div key={`filter-${Math.random()}-${index}`}>{filter}</div>
      ))}

      <div className="flex-1" />

      {/* Export will be handled by individual report components */}
    </div>
  );
};
